import classNames from 'classnames';
import { type ComponentPropsWithoutRef, memo } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { getAvatarRandomStyle } from '../utils/getAvatarRandomStyle';
import { getAvatarTextClassName } from '../utils/getAvatarTextClassName';

export const avatar = tailwindVariants({
  base: 'relative flex shrink-0 cursor-default items-center justify-center overflow-hidden bg-grey-3 object-cover text-grey-12',
  defaultVariants: {
    appearance: 'circle',
    disabled: false,
  },
  variants: {
    appearance: {
      circle: 'rounded-full',
      square: 'rounded-4',
    },
    disabled: {
      true: 'opacity-70 grayscale',
    },
  },
});

export type AvatarVariants = VariantProps<typeof avatar>;

export type AvatarSizes = 16 | 20 | 24 | 28 | 32 | 36 | 40 | 48 | 56 | 96;

export type AvatarProps = ComponentPropsWithoutRef<'div'> &
  AvatarVariants & {
    size?: AvatarSizes;
    text?: string;
    colorSeed?: string | null;
  } & (
    | {
        alt: string;
        src: string | null;
      }
    | {
        alt?: string;
        src?: string | null;
      }
  );

export const Avatar = memo(
  ({
    alt,
    appearance,
    size = 40,
    src,
    text,
    disabled = false,
    colorSeed,
    className,
    style,
    ...restOfProps
  }: AvatarProps) => (
    <div
      className={avatar({ appearance, class: className, disabled })}
      style={{
        ...(colorSeed ? getAvatarRandomStyle(colorSeed) : undefined),
        height: size,
        width: size,
        ...style,
      }}
      {...(!src && text && alt && { 'aria-label': alt, role: 'img' })}
      {...restOfProps}
    >
      {src ? (
        <img alt={alt} className="block object-cover" src={src} style={{ height: size, width: size }} />
      ) : text ? (
        <p className={classNames('font-bold', getAvatarTextClassName(size))}>{text}</p>
      ) : (
        <svg className="size-full text-grey-5" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
          <title>Avatar</title>
          <path
            d="M16 19a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0 26c6.627 0 12-5.373 12-12s-5.373-12-12-12S4 26.373 4 33s5.373 12 12 12Z"
            fill="currentColor"
            fillRule="evenodd"
          />
        </svg>
      )}
    </div>
  ),
);

Avatar.displayName = 'Avatar';
